import { useForm as createUseForm } from "@ant-design-vue/use";
import { Button, Col, notification, Row } from "ant-design-vue";
import { defineAsyncComponent, reactive } from "vue";
import { addModal, setIsLoading as setIsLoadingApp, removeModal } from "../../../app.logic";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import { createConditionAction, createConditionForm } from "./profile.condition";
import lodash from "lodash";
import i18n from "../../../i18n";
import store from "../../../store";
import { createButtonSave } from "../../../utils/button";
import { modalStyleForm } from "../../../utils/constant";
import { initUseForm, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { has } from "../../../utils/menu-context";
import { required, validateError, requiredWhen, email, thPid } from "../../../utils/validate";
import { createForm as createFormUser } from "../setting/user-mgt/user/user.logic";
import { createField } from "./profile.section";
import { callAddressType, createForm as createFormAddress } from "../../../components/address/address.logic";
import { callGender, callPersonType, callSameAddressType } from "../../account/register/register.section";

const ChangePassword = defineAsyncComponent(() => import("./ChangePassword"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/user";

export const callMeProfile = () => store.dispatch(`${actionPrefix}/meProfile`, {});
export const callMeProfileUpdate = ({ form }) => store.dispatch(`${actionPrefix}/meProfileUpdate`, { data: form });
export const callDepartment = ({ searchQuery }) => store.dispatch("resource/v1/master/department/cacheFindAll", { searchQuery });

export const onCallMeProfile = () => {
    setIsLoading(true);
    return callMeProfile().finally(() => setIsLoading(false));
};

export const onCallMeProfileUpdate = ({ props, data, refs }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callMeProfileUpdate({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(() => {
                    loadForm({ props, data, refs });
                    return notification["success"]({ message: i18n.global.t("views.profile.success") });
                });
        })
        .catch((error) => validateError({ error }));
};

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refPersonType`]: [requiredWhen({ when: () => "PEOPLE" != data.registerType && "OFFICER" != data.registerType })],
        [`${prefix}refCompanyType`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}companyNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}registerCapital`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}idCard`]: [required, thPid],
        [`${prefix}birthDate`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refNationality`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refRace`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refGender`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refTitle`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}firstNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}lastNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refEducation`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refMainOccupation`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}refSecondOccupation`]: [requiredWhen({ when: () => model.refPersonType?.key != "1" })],
        [`${prefix}positionCode`]: [requiredWhen({ when: () => model.isOfficer })],
        [`${prefix}refPosition`]: [requiredWhen({ when: () => model.isOfficer })],
        [`${prefix}refDepartment`]: [requiredWhen({ when: () => model.isOfficer })],
        [`${prefix}refSubDepartment`]: [requiredWhen({ when: () => model.isOfficer })],
        [`${prefix}refSameAddressType`]: [requiredWhen({ when: () => "PEOPLE" === data.registerType })],
        [`${prefix}mobile`]: [required],
        [`${prefix}email`]: [requiredWhen({ when: () => model.refPersonType?.key != "2" }), email],
    };
    model?.addresses?.forEach((item, index) => {
        if (item.isDelete !== true && "PEOPLE" != data.registerType) {
            rules[`${prefix}addresses.${index}.refAddressType`] = [required];
            rules[`${prefix}addresses.${index}.noTh`] = [required];
            rules[`${prefix}addresses.${index}.soiTh`] = [required];
            rules[`${prefix}addresses.${index}.roadTh`] = [required];
            // rules[`${prefix}addresses.${index}.mooTh`] = [requiredWhen({ when: () => model?.addresses[index]?.refProvince?.code !== "10" })];
            rules[`${prefix}addresses.${index}.refProvince`] = [required];
            rules[`${prefix}addresses.${index}.refAmphur`] = [required];
            rules[`${prefix}addresses.${index}.refTambon`] = [required];
            rules[`${prefix}addresses.${index}.postCode`] = [required];
        }
    });
    model?.attachmentDocs?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachmentDocs.${index}`] = [requiredWhen({ when: () => model.isOfficer === true })];
            rules[`${prefix}attachmentDocs.${index}.name`] = [requiredWhen({ when: () => model.isOfficer === true })];
        }
    });
    model?.attachmentIdCards?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachmentIdCards.${index}`] = [
                requiredWhen({ when: () => model.refPersonType?.key === "2" || "OFFICER" === data.registerType }),
            ];
            rules[`${prefix}attachmentIdCards.${index}.name`] = [
                requiredWhen({ when: () => model.refPersonType?.key === "2" || "OFFICER" === data.registerType }),
            ];
        }
    });
    model?.attachmentSignatures?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachmentSignatures.${index}`] = [
                requiredWhen({ when: () => model.refPersonType?.key === "2" || "OFFICER" === data.registerType }),
            ];
            rules[`${prefix}attachmentSignatures.${index}.name`] = [
                requiredWhen({ when: () => model.refPersonType?.key === "2" || "OFFICER" === data.registerType }),
            ];
        }
    });
    return rules;
};

export const createFormTitle = () => {
    return i18n.global.t("button.profile");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = callMeProfile().then((user) => ({ data: [user] }));
    }
    return promise
        .then((page) => initUseForm({ data, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};
export const loadDefaultForm = ({ data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const userInfo = store.state.resource.auth.info;
    const promises = [callPersonType({}), callAddressType({}), callGender({}), callSameAddressType({})];
    return Promise.all(promises).then((arr) => {
        data.personTypes = arr[0];
        data.addressTypes = arr[1];
        data.genderTypes = arr[2];
        data.callSameAddressType = arr[3];
        data.myUserRole = userInfo.roles;
        if (!userInfo) {
            modelRef.refPersonType = data.personTypes.find((d) => d.key === "1");
            modelRef.refSameAddressType = data.callSameAddressType.find((d) => d.key === "2");
            modelRef.refGender = data.genderTypes.find((d) => d.key === "M");
        }
        data.myUserRole?.forEach((item) => {
            const role = item.refRole;
            modelRef.userRolesProfile.push({ refRole: role });
        });
        data.registerType = formRegisterType({ modelRef });
        if (!["1"].includes(modelRef.refPersonType?.key)) {
            modelRef.addresses.forEach((d) => {
                if (["1"].includes(d.refAddressType?.key)) {
                    d.isValue1 = false;
                }
            });
        }
        if ("OFFICER" != data.registerType) {
            modelRef.addresses.forEach((d) => {
                if (["2"].includes(d.refAddressType?.key)) {
                    d.isValue1 = true;
                }
            });
        }
    });
};

export const createForm = ({ props, data, refs }) => {
    return {
        ...createFormUser({ props, data, refs }),
        addresses: [createFormAddress()],
        userRolesProfile: [],
        acceptCondition: null,
    };
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => ({
            length1: data.useForm.modelRef.attachments.filter((d) => d.isDelete !== true).length,
            length2: data.useForm.modelRef.attachmentDocs.filter((d) => d.isDelete !== true).length,
            length3: data.useForm.modelRef.attachmentIdCards.filter((d) => d.isDelete !== true).length,
            length4: data.useForm.modelRef.attachmentSignatures.filter((d) => d.isDelete !== true).length,
            length5: data.useForm.modelRef.addresses.filter((d) => d.isDelete !== true).length,
        }),
        createRules: () => createRules({ props, data, refs }),
        itemsKey: ["attachments", "attachmentDocs", "attachmentIdCards", "attachmentSignatures", "addresses"],
    });
    loadForm({ props, data, refs }).then(() => {
        setAppPageHeader({
            slots: {
                default: () => createAppPageHeaderDefault({ props, data, refs }),
            },
        });
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const col1 = [];
    const col2 = [];
    const { menuContext } = props;
    const personTypeByKey = modelRef.refPersonType?.key
    col1.push(createButtonSave({ onClick: () => onCallMeProfileUpdate({ props, data, refs }), classStyle: "m-1" }));
    if (has({ menuContext, key: "canChangePassword" })) {
        col2.push(
            <Button
                class="btn-warning m-1"
                size="small"
                onClick={() => openModalChangePassword({ props: { dataProp: { mobile: modelRef.mobile, email: modelRef.email, personTypeByKey: personTypeByKey } }, data, refs })}
            >
                <span>{i18n.global.t("button.changePassword")}</span>
            </Button>
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalChangePassword = ({ props, refs }) => {
    const options = addModal({
        bind: {
            ...modalStyleForm,
            title: i18n.global.t("views.changePassword.title"),
            width: "520px",
            okText: i18n.global.t("button.changePassword"),
            maskClosable: false,
            closable: false,
            footer: () => modelFooterButton({ refs: refs, options }),
            // onOk: () => {
            //     refs.changePassword.onSubmit().then(() => {
            //         notification["success"]({ message: i18n.global.t("views.changePassword.success") });
            //         options.bind.onCancel();
            //         options.bind.visible = false;
            //     });
            // },
            // onCancel: () => {
            //     if (refs.changePassword?.data.isGenOtp === true) {
            //         options.bind.visible = false;
            //     }
            // },
        },
        render: () => (
            <ChangePassword
                ref={(e) => {
                    if (e) refs.changePassword = e;
                }}
                {...props}
            ></ChangePassword>
        ),
    });
    return options;
};

export const state = reactive({
    conditionAction: createConditionAction(),
});

export const formRegisterType = ({ modelRef }) => {
    if (modelRef.isOfficer == true) {
        return "OFFICER";
    }
    const registerType = modelRef.userRolesProfile?.find((d) => {
        return "FISHERMAN" === d.refRole?.name || "PEOPLE" === d.refRole?.name;
    });
    return registerType.refRole?.name ? registerType.refRole?.name : "";
};

export const modelFooterButton = ({ refs, options }) => {
    const onOk = () => {
        refs.changePassword.onSubmit().then(() => {
            notification["success"]({ message: i18n.global.t("views.changePassword.success") });
            options.bind.onCancel();
            options.bind.visible = false;
        });
    }
    const onCancel = () => {
        if (refs.changePassword?.data.isGenOtp === true) {
            //options.bind.visible = false;
            removeModal({ options })
        }
    }
    return (<div><Button disabled={refs.changePassword?.data?.isGenOtp === false } onClick={onCancel}>Cancel</Button><Button type="primary" onClick={onOk}>OK</Button></div>)
};
